import { DataManager } from '@syncfusion/ej2-data';
import { ImportConfigurationProtocolService } from './import-configuration-protocol.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { CommandClickEventArgs, CommandModel, RecordDoubleClickEventArgs, GridModule } from '@syncfusion/ej2-angular-grids';
import { DialogComponent as SyncfusionDialogComponent, TooltipModule, DialogModule } from '@syncfusion/ej2-angular-popups';
import { TooltipHelper } from 'src/app/base/shared/tooltip-helper';
import { ImportConfigurationEntryState, ImportProtocolDTO, ImportSuccessState, NavisionImportTemplate } from 'src/app/base/models/models.swagger.generated';
import { DatePipe } from '@angular/common';
import { ImportConfigurationProtocolEditFormComponent } from './import-configuration-protocol-edit-form/import-configuration-protocol-edit-form.component';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { importConfigurationProtocolRoute } from 'src/app/core/routing/route-names';
import { UrlHelper } from 'src/app/base/shared/url-helper';
import * as pages from '../../../core/routing/page-names';
import { NavisionImportTemplateEnumFilter } from '../navision-import-template-enum-filter';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';

export type SuccessStateEnumFilterType = {
  successState: ImportSuccessState;
}

@Component({
    selector: 'app-import-configuration-protocol',
    templateUrl: './import-configuration-protocol.component.html',
    styleUrls: ['./import-configuration-protocol.component.scss'],
    imports: [TitleBarComponent, GridModule, QueryBuilderModule, TooltipModule, DialogModule, ImportConfigurationProtocolEditFormComponent, DatePipe, TranslateModule]
})
export class ImportConfigurationProtocolComponent extends GridBaseComponent<any> implements OnInit {
  @ViewChild('logEntryDialog')
  public logEntryDialog: SyncfusionDialogComponent;

  public SuccessState = ImportSuccessState;
  public EntryState = ImportConfigurationEntryState;
  public dateFilter = GridConstantsFactory.createDateFilter();
  public successStateFilter = GridConstantsFactory.createCheckBoxFilter();
  public protocol: ImportProtocolDTO;
  public selectedProtocolId: number;
  public NavisionImportTemplate = NavisionImportTemplate;

  public checkBoxFilter = GridConstantsFactory.createCheckBoxFilter();

  public get hasError(): boolean {
    return this.protocol?.successState === ImportSuccessState.Error;
  }

  constructor(
    translateService: TranslateService,
    private importProtocolService: ImportConfigurationProtocolService,
    private spinnerService: SpinnerService,
    private router: Router) {
    super(translateService, importProtocolService);
    this.editSettings = {
      allowEditing: false,
      allowAdding: false,
      allowDeleting: false,
      mode: 'Dialog',
      showDeleteConfirmDialog: false
    };
    this.gridCommands = [{
      type: 'None',
      title: this.translateService.instant('i18n.shared.details'),
      buttonOption:
      {
        cssClass: 'icon-btn grid-icon',
        iconCss: 'e-icons'
      }
    } as CommandModel];
  }

  ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.importProtocolService, this.spinnerService)
    });
    this.translateService.get('i18n.shared.details').subscribe((t) => {
      this.gridCommands[0].title = t;
    });

    this.editDialogTitle = 'i18n.domain.import-protocol.titleEdit';
    // TODO check if this is needed and needs to be added to grid base
    // this.dialogConfig.width = 960;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd && event.url.includes(importConfigurationProtocolRoute))).subscribe(() => {
      this.urlChanged();
    });

    // call the first time urlChanged on our own
    this.urlChanged();
  }

  public urlChanged(): void {
    var id = UrlHelper.getIdForSegment(pages.importConfigurationsProtocolPageName, this.router)
    if (id != null){
      this.importProtocolService.selectedProtocolId = id;
      this.selectedProtocolId = id;
    } else {
      this.selectedProtocolId = null;
      this.importProtocolService.selectedProtocolId = null;
    }
  }

  public onDataBound() {
    if (this.selectedProtocolId != null) {
      const rowIndex = this.grid.getRowIndexByPrimaryKey(this.selectedProtocolId);
      if (rowIndex < 0) {
        // data is not ready yet
        return;
      }
      const rowElement = this.grid.getRowByIndex(rowIndex);
      if (!rowElement) {
        return;
      }
      const rowInfo = this.grid.getRowInfo(rowElement);
      if (rowInfo) {
        this.commandClick(rowInfo);
      }
      this.grid.selectRow(rowIndex);
    }
  }


  public rowDataBound(args) {
    const protocol = args.data as ImportProtocolDTO;
    const buttonElement = args.row.querySelector('.e-unboundcelldiv')?.lastElementChild;
    if (protocol.successState === ImportSuccessState.Error) {
      buttonElement?.firstElementChild?.classList.add('icon-debug', 'icon-warning-color');
    } else {
      buttonElement?.firstElementChild?.classList.add('icon-information');
    }
  }

  public onClose() {
    this.logEntryDialog.hide();
    this.protocol = null;
    this.selectedProtocolId = null;
    this.updateUrl(null);
    this.grid.selectRows([]);
  }

  public onSetAsFixed() {
    if (this.protocol.successState !== ImportSuccessState.Error) {
      return;
    }
    this.importProtocolService.setProtocolAsFixed(this.protocol.id).subscribe(() => {
      this.protocol = null;
      this.logEntryDialog.hide();
      this.grid.refresh();
    });
  }

  public commandClick(args: CommandClickEventArgs) {
    const protocol = args.rowData as ImportProtocolDTO;
    this.selectedProtocolId = protocol.id;
    this.updateUrl(protocol);
    this.openLogEntryDialog(protocol);
  }

  public onRecordDoubleClick(args: RecordDoubleClickEventArgs) {
    const protocol = args.rowData as ImportProtocolDTO;
    this.selectedProtocolId = protocol.id;
    this.updateUrl(protocol);
    this.openLogEntryDialog(protocol);
  }

  public onRefresh() {
    this.grid.refresh();
  }

  //try to extract json content from the message.
  public extractJsonMessage(data: string, onlyContent: boolean, maxChar:number): string {
    return TooltipHelper.extractJsonMessage(data, onlyContent, maxChar);
  }

  protected initializeEditForm(data: ImportProtocolDTO): void {
    this.protocol = data;
  }

  protected getEditDialogTitleParams(rowData: any) {
    return null;
  }

  private openLogEntryDialog(protocol: ImportProtocolDTO) {
    this.protocol = protocol;
    this.logEntryDialog.show();
  }

  private updateUrl(selectedProtocol: ImportProtocolDTO) {
    if (selectedProtocol?.id != null) {
      this.router.navigate([importConfigurationProtocolRoute + "/" + selectedProtocol.id]);
    } else {
      this.router.navigate([importConfigurationProtocolRoute]);
    }
  }

  public formatFilterNavisionImportTemplate(navisionImportTemplateEnumFilter: NavisionImportTemplateEnumFilter): string {
    if (!navisionImportTemplateEnumFilter) {
      return '';
    }
    return this.translateService.instant('i18n.domain.import-configuration.enums.navision-import-template.' + NavisionImportTemplate[navisionImportTemplateEnumFilter.navisionImportTemplate]);
  }
}
